import type { TokenScope } from '@accounting/shared';
import type { MessageKey } from '../i18n/da.js';
import {
  Alert,
  Button,
  Card,
  Center,
  Checkbox,
  Group,
  List,
  Loader,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useAuth } from '../auth/useAuth.js';
import { ErrorMessage } from '../components/ErrorMessage.js';
import {
  useApproveAuthorization,
  useAuthorizationRequest,
  useDenyAuthorization,
} from '../api/hooks.js';
import { useT } from '../i18n/useT.js';

/**
 * The consent screen — the one moment a person is in the loop.
 *
 * A client asked for access; this is where somebody who can actually grant it
 * says yes or no. See ADR-008.
 *
 * ## Why it is not inside AppLayout
 *
 * No sidebar, no company switcher, no navigation. Two reasons, and the second
 * is the real one:
 *
 *  - A person arrives here from another application, does one thing, and leaves
 *    again. Wrapping that in the whole application's chrome invites them to
 *    wander off mid-flow into a screen the client is still waiting on.
 *  - **The company switcher would contradict this page.** The switcher sets
 *    which company *you* are looking at; the select below sets which company
 *    the *client's reads* default to. Two company pickers on one screen, one of
 *    which quietly changes the other's meaning, is a way to grant access to the
 *    wrong set of books while reading the right name.
 *
 * ## The client's name is the client's own word
 *
 * `clientName` comes from an unauthenticated registration call: anyone may
 * register a client called "Regnskab support". React escapes it, so it cannot
 * become markup — but nothing can make it *true*, which is why the screen also
 * shows where the person will be sent back to. The host is the part that cannot
 * be faked past the exact-match check on redirect URIs.
 */
export function OauthConsentPage(): ReactNode {
  const t = useT();
  const [params] = useSearchParams();
  const requestId = params.get('request') ?? '';
  const { companyId: activeCompanyId } = useAuth();

  const request = useAuthorizationRequest(requestId);
  const approve = useApproveAuthorization();
  const deny = useDenyAuthorization();
  const [chosen, setChosen] = useState<string | null>(null);
  const [scopes, setScopes] = useState<string[] | null>(null);

  if (requestId === '') {
    return (
      <Shell>
        <Alert color="yellow" title={t('consent.missingTitle')} variant="light">
          <Text size="sm">{t('consent.missingBody')}</Text>
        </Alert>
      </Shell>
    );
  }

  if (request.isLoading) {
    return (
      <Shell>
        <Center>
          <Loader />
        </Center>
      </Shell>
    );
  }

  if (request.error !== null || request.data === undefined) {
    return (
      <Shell>
        <ErrorMessage error={request.error} title={t('consent.goneTitle')} />
        <Text size="sm" c="dimmed">
          {t('consent.goneBody')}
        </Text>
      </Shell>
    );
  }

  const pending = request.data;
  /*
    The company the person is already looking at, when they are a member of it.
    Not a shortcut: it is the company they had in mind when they started this,
    and it is the one answer that will not surprise them. They can change it,
    and with one membership there is nothing to change.
  */
  const fallback =
    pending.companies.find((company) => company.companyId === activeCompanyId)
      ?.companyId ??
    pending.companies[0]?.companyId ??
    null;
  const companyId = chosen ?? fallback;
  const busy = approve.isPending || deny.isPending;

  /*
    What the client asked for, pre-ticked, and the person may clear any of it.
    Granting less than was requested is the point of showing this at all: the
    API intersects whatever comes back with the request, so this can narrow and
    never widen.
  */
  const granted = scopes ?? [...pending.requestedScopes];

  const leave = (redirectTo: string): void => {
    // A full navigation, not a router push: the destination belongs to the
    // client that sent us here, and is outside this application entirely.
    window.location.assign(redirectTo);
  };

  return (
    <Shell>
      <Title order={2}>{t('consent.title', { client: pending.clientName })}</Title>
      <Text size="sm">{t('consent.intro')}</Text>

      <Checkbox.Group
        label={t('consent.scopesLabel')}
        description={t('consent.scopesHelp')}
        value={granted}
        onChange={setScopes}
      >
        <Stack gap="xs" mt="xs">
          {pending.requestedScopes.map((scope) => (
            <Checkbox
              key={scope}
              value={scope}
              disabled={busy}
              label={t(`scope.${scope}` as MessageKey)}
              description={t(`scope.${scope}Help` as MessageKey)}
            />
          ))}
        </Stack>
      </Checkbox.Group>

      <List size="sm" spacing="xs">
        <List.Item>{t('consent.can.role')}</List.Item>
        <List.Item>{t('consent.can.audit')}</List.Item>
      </List>

      <Select
        label={t('consent.companyLabel')}
        description={t('consent.companyHelp')}
        data={pending.companies.map((company) => ({
          value: company.companyId,
          label:
            company.name === null
              ? `${company.companyId.slice(0, 8)} · ${company.role}`
              : `${company.name} · ${company.role}`,
        }))}
        value={companyId}
        onChange={setChosen}
        allowDeselect={false}
        disabled={busy}
      />

      <Text size="xs" c="dimmed">
        {t('consent.returnsTo', { host: hostOf(pending.redirectUri) })}
      </Text>

      {approve.error !== null || deny.error !== null ? (
        <ErrorMessage
          error={approve.error ?? deny.error}
          title={t('consent.failedTitle')}
        />
      ) : null}

      <Group justify="flex-end">
        <Button
          variant="default"
          disabled={busy}
          onClick={() => {
            deny.mutate(requestId, { onSuccess: (result) => leave(result.redirectTo) });
          }}
        >
          {t('consent.deny')}
        </Button>
        <Button
          disabled={busy || companyId === null}
          loading={approve.isPending}
          onClick={() => {
            if (companyId === null) {
              return;
            }
            approve.mutate(
              { id: requestId, companyId , scopes: granted as TokenScope[]},
              { onSuccess: (result) => leave(result.redirectTo) },
            );
          }}
        >
          {t('consent.allow')}
        </Button>
      </Group>

      <Text size="xs" c="dimmed">
        {t('consent.revokeHint')}
      </Text>
    </Shell>
  );
}

function Shell({ children }: { children: ReactNode }): ReactNode {
  return (
    <Center mih="100dvh" p="md">
      <Card withBorder padding="lg" maw={520} w="100%">
        <Stack gap="md">{children}</Stack>
      </Card>
    </Center>
  );
}

/** The host alone: the part of the callback a person can recognise. */
function hostOf(redirectUri: string): string {
  try {
    return new URL(redirectUri).host;
  } catch {
    return redirectUri;
  }
}
